<script lang="ts">
  // (c) Cognisoft Aps, 2022

  import { db } from "../firebase";
  import { doc, updateDoc } from "@firebase/firestore";

  import { IconButtonType, DocType, Level, Role } from "../enums";

  import {
    minHelpDelaySecs,
    maxHelpDelaySecs,
    helpDelaySecsStep,
    minShowRepeatButtonDelaySecs,
    maxShowRepeatButtonDelaySecs,
    showRepeatButtonSecsStep,
    minSessionsBeforeLevelChange,
    maxSessionsBeforeLevelChange,
  } from "../constants";

  import { editPWASettings } from "../stores/edit-pwa-settings.svelte";

  import { route } from "../stores/route.svelte";

  import { icons, Icons } from "../utilities/icons";

  import ChangeLevelModal from "../modals/ChangeLevelModal.svelte";

  import IconButton from "../components/IconButton.svelte";
  import MessageModal from "../modals/MessageModal.svelte";

  // STATE ------------------------------------------

  let showChangeLevelModal = $state(false);
  let showErrorModal = $state(false);
  let errorMessage = $state("");
  let manualLevelShift = $state(false);
  let previousLevel = editPWASettings.level; // detect manual level change in modal

  // FUNCTIONS --------------------------------------

  const changeLevelClicked = () => {
    showChangeLevelModal = true;
  };

  const changeLevelModalClosed = () => {
    showChangeLevelModal = false;
    // if level was changed:
    if (previousLevel !== editPWASettings.level) {
      editPWASettings.completedOnCurrentLevel = 0;
      editPWASettings.settings.doTestNextSession = true;
    }
  };

  const saveSettingsAndLeavePage = async () => {
    const documentRef = doc(db, DocType.Users, editPWASettings.email);
    try {
      await updateDoc(documentRef, {
        active: editPWASettings.active,
        name: editPWASettings.name,
        currentLevel: editPWASettings.level,
        completedOnCurrentLevel: editPWASettings.completedOnCurrentLevel,
        settings: editPWASettings.settings,
      });
    } catch (err) {
      console.error(
        "Kunne ikke opdatere indstillinger: ",
        editPWASettings.email,
        " fejl: ",
        err,
      );
      (errorMessage =
        "Kunne ikke opdatere indstillinger, rapporter venligst til mail@cognisoft.dk, fejl: "),
        err;
      showErrorModal = true;
    } finally {
      route.current = route.return;
    }
  };
</script>

<!-- HTML ===================================================== -->

<main data-page="pwa-edit-settings">
  <div class="heading">
    <svg class="icon" viewBox={icons[Icons.CogLight].viewBox}>
      {@html icons[Icons.CogLight].path}
    </svg>
    <h1>Indstillinger</h1>
  </div>
  <div>
    <hr />
  </div>
  <div class="settings">
    <div class="input">
      <label for="name">Kalde-navn:</label>
      <input type="text" id="name" bind:value={editPWASettings.name} />
    </div>
    <hr />
    <div class="first-options">
      {#if editPWASettings.pwaRole !== Role.Private}
        <div class="check">
          <input
            type="checkbox"
            id="active"
            bind:checked={editPWASettings.active}
            disabled={!editPWASettings.active &&
              !editPWASettings.hasFreeLicenses}
          />
          <label for="active">Aktiv</label>
        </div>
      {/if}
      <div
        class="check"
        class:qwerty-checkbox={editPWASettings.pwaRole !== Role.Private}
      >
        <input
          type="checkbox"
          id="qwerty"
          bind:checked={editPWASettings.settings.qwertyKeyboard}
        />
        <label for="qwerty">QWERTY-tastatur</label>
      </div>
    </div>
    <hr />
    <div class="check">
      <input
        type="checkbox"
        id="psw"
        bind:checked={editPWASettings.settings.perseveration}
      />
      <label for="psw">Tag højde for perseveration</label>
    </div>
    <div class="check">
      <input
        type="checkbox"
        id="spelling"
        bind:checked={editPWASettings.settings.simpleSpelling}
      />
      <label for="spelling">Kun let stavetrin (kun første af tre trin)</label>
    </div>
    <div class="gauge">
      <label for="pause-range">
        Tid før hjælp: {editPWASettings.settings.helpDelaySecs} sekunder
      </label>
      <input
        type="range"
        id="pause-range"
        class="range"
        min={minHelpDelaySecs}
        max={maxHelpDelaySecs}
        step={helpDelaySecsStep}
        bind:value={editPWASettings.settings.helpDelaySecs}
      />
    </div>
    <div class="gauge">
      <label for="repeat-delay-range">
        Tid før gentag-knap aktiv: {editPWASettings.settings.repeatDelaySecs}
        {editPWASettings.settings.repeatDelaySecs === 1 ? "sekund" : "sekunder"}
      </label>
      <input
        type="range"
        id="repeat-delay-range"
        class="range"
        min={minShowRepeatButtonDelaySecs}
        max={maxShowRepeatButtonDelaySecs}
        step={showRepeatButtonSecsStep}
        bind:value={editPWASettings.settings.repeatDelaySecs}
      />
    </div>
    <hr />
    <div class="check">
      <input
        type="checkbox"
        id="test"
        bind:checked={editPWASettings.settings.doIntroNextSession}
      />
      <label for="test">Næste gennemgang er introduktion.</label>
    </div>
    <div class="check">
      <input
        type="checkbox"
        id="test"
        disabled={editPWASettings.settings.doIntroNextSession}
        bind:checked={editPWASettings.settings.doTestNextSession}
      />
      <label for="test">Baseline/statustest ved næste gennemgang.</label>
    </div>
    <div>
      <p class="italics">
        Aktuelt niveau:
        {#if editPWASettings.level === Level.First}
          Niveau 1.
        {:else if editPWASettings.level === Level.Second}
          Niveau 2.
        {:else if editPWASettings.level === Level.SecondVerb}
          Niveau 3 - Verbum udvidelse.
        {:else if editPWASettings.level === Level.SecondSubject}
          Niveau 4 - Subjekt udvidelse.
        {/if}
        Antal gennemførte på dette niveau: {editPWASettings.completedOnCurrentLevel}
      </p>
    </div>
    <div class="check">
      <input type="checkbox" id="levelshift" bind:checked={manualLevelShift} />
      <label for="levelshift">Manuel niveau skift.</label>
    </div>
    <div>
      {#if manualLevelShift}
        <IconButton
          onclick={changeLevelClicked}
          type={IconButtonType.Button}
          textMediumScaled={true}
        >
          {#snippet message()}Skift niveau{/snippet}
        </IconButton>
      {/if}
    </div>
    <hr />
    <div class="gauge">
      <label for="levelchangesessions">
        Sessioner før automatisk niveauskift: {editPWASettings.settings
          .sessionsBeforeLevelChange}
      </label>
      <input
        type="range"
        id="levelchangesessions"
        class="range"
        min={minSessionsBeforeLevelChange}
        max={maxSessionsBeforeLevelChange}
        step="1"
        bind:value={editPWASettings.settings.sessionsBeforeLevelChange}
      />
    </div>
  </div>

  <div class="bottom-area">
    <span class="button-spacing">
      <IconButton
        onclick={() => (route.current = route.return)}
        type={IconButtonType.Button}
        textMediumScaled={true}
        icon={Icons.WindowClose}
      >
        {#snippet message()}Fortryd{/snippet}
      </IconButton>
    </span>
    <span class="button-spacing save-button">
      <IconButton
        onclick={saveSettingsAndLeavePage}
        type={IconButtonType.Button}
        textMediumScaled={true}
        icon={Icons.Check}
      >
        {#snippet message()}Gem{/snippet}
      </IconButton>
    </span>
  </div>
</main>

{#if showChangeLevelModal}
  <ChangeLevelModal changeLevelModalOK={changeLevelModalClosed} />
{/if}

{#if showErrorModal}
  <MessageModal
    closeModal={() => {
      showErrorModal = false;
    }}
  >
    {errorMessage}
  </MessageModal>
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    display: flex;
    flex-direction: column;
    height: calc(var(--vh, 1vh) * 100);
    padding: calc(var(--std-margin) * 2);
  }
  h1 {
    font-size: 4vmin;
    margin-left: var(--std-margin);
    margin-bottom: var(--std-margin);
  }
  p,
  label {
    font-size: 3vmin;
    line-height: 5vmin;
  }
  .first-options {
    display: flex;
  }
  .qwerty-checkbox {
    padding-left: calc(var(--std-margin) * 2);
  }
  .heading {
    display: flex;
    align-items: center;
  }
  .settings {
    flex-grow: 1;
    overflow: scroll;
  }
  .icon {
    height: min(calc(var(--vh, 1vh) * 5), 5vmin);
    width: min(calc(var(--vh, 1vh) * 5), 5vmin);
  }
  .range {
    width: 40vw;
  }
  input[type="range"] {
    height: 1vmin;
  }
  .mail {
    font-size: 3vmin;
  }
  .gauge {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  input[type="text"] {
    font-size: 2.5vmin;
    width: 70%;
  }
  input[type="checkbox"] {
    margin-right: 1vmin;
    /* font-size: 2.5vmin; */
  }
  .bottom-area {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border-top: 2px solid var(--key-hover-border-color);
  }
  .button-spacing {
    margin-top: var(--std-margin);
    margin-right: var(--std-margin);
  }
  .save-button {
    margin-left: auto;
    margin-right: 0;
  }
  .italics {
    font-style: italic;
    opacity: 0.8;
    font-size: 2.3vmin;
  }
</style>
